import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  Store,
  MapPin,
  Package,
  Plus,
  Trash2,
  Edit3,
  ChevronRight,
  Save,
  X,
  RefreshCcw,
  Info,
  LayoutGrid,
  Layers,
  Building2,
  Settings2,
  Target,
  Search,
  Eye,
  EyeOff,
  List,
  Archive,
  CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Gestión de Sitios (Sucursales/Depósitos) - Estándar Impulza Enterprise
 * Refactorizado al estilo Productos.jsx
 */
const Sitios = () => {
  const { profile, organization } = useAuth();

  // --- Terminales Point de Mercado Pago -------------------------------------
  // Se listan desde la cuenta del comercio (GET /terminals/v1/list) en vez de
  // pedirle al usuario que tipee un id tipo NEWLAND_N950__N950NCB801293324.
  // Además mostramos el operating_mode: una terminal en STANDALONE IGNORA las
  // órdenes que se le mandan por API, y es la causa nº1 de "mandé el cobro y la
  // terminal no hace nada".
  const [terminales, setTerminales] = useState([]);
  const [cargandoTerminales, setCargandoTerminales] = useState(false);
  const [terminalesConsultadas, setTerminalesConsultadas] = useState(false);

  const cargarTerminales = async () => {
    if (!profile?.organization_id) return;
    setCargandoTerminales(true);
    try {
      const { data, error } = await supabase.functions.invoke('mp-point-terminals', {
        body: { organizationId: profile.organization_id, action: 'list' },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error);
      setTerminales(data?.terminals || []);
      setTerminalesConsultadas(true);
      if (!(data?.terminals || []).length) {
        toast.error('No se encontraron terminales Point en la cuenta de Mercado Pago.');
      }
    } catch (err) {
      console.error('Error listando terminales Point:', err);
      toast.error(`No se pudieron listar las terminales: ${err.message}`);
    } finally {
      setCargandoTerminales(false);
    }
  };

  const ponerEnModoPdv = async (terminalId) => {
    if (!profile?.organization_id || !terminalId) return;
    setCargandoTerminales(true);
    try {
      const { data, error } = await supabase.functions.invoke('mp-point-terminals', {
        body: { organizationId: profile.organization_id, action: 'set-pdv', terminalId },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error);
      toast.success('Terminal en modo PDV. Reiniciala para que tome el cambio.');
      setTerminales((prev) => prev.map((t) => (
        t.id === terminalId ? { ...t, operating_mode: 'PDV' } : t
      )));
    } catch (err) {
      console.error('Error configurando el modo PDV:', err);
      toast.error(`No se pudo poner la terminal en modo PDV: ${err.message}`);
    } finally {
      setCargandoTerminales(false);
    }
  };


  const topbarOffsetClass = 'top-16';

  const [sitios, setSitios] = useState([]);
  const [centros, setCentros] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [usePosPriceListPerSite, setUsePosPriceListPerSite] = useState(false);
  const [loading, setLoading] = useState(true);

  // UI states
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Modelo Productos: visibilidad + filtros + layout
  const [viewMode, setViewMode] = useState('activos'); // activos | archivados
  const [layout, setLayout] = useState('lista'); // lista | grilla
  const [busqueda, setBusqueda] = useState('');
  const [filtroCentro, setFiltroCentro] = useState('todos');
  const [filtroDeposito, setFiltroDeposito] = useState('todos'); // todos | con_depositos | sin_depositos

  // Form states
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    depositos: [],
    cost_center_id: '',
    mp_pos_id: '',
    mp_terminal_id: '',
    price_list_id: ''
  });

  // Va DESPUÉS del useState de formData a propósito: el array de dependencias se
  // evalúa al llamar useMemo, así que declararlo antes rompe por TDZ (el
  // optional chaining no protege) y la pantalla no renderiza.
  const terminalSeleccionada = useMemo(
    () => terminales.find((t) => t.id === formData?.mp_terminal_id) || null,
    [terminales, formData?.mp_terminal_id],
  );

  const [newDeposito, setNewDeposito] = useState('');

  useEffect(() => {
    if (profile?.organization_id) {
      fetchData();
    }
  }, [profile]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, c, pl] = await Promise.all([
        supabase.from('sitios').select('*, centros_costo(nombre)').eq('organization_id', profile.organization_id).order('nombre'),
        supabase.from('centros_costo').select('*').eq('organization_id', profile.organization_id).order('nombre'),
        supabase.from('price_lists').select('id, name, is_active').eq('organization_id', profile.organization_id).eq('is_active', true).order('name'),
      ]);
      setSitios(s.data || []);
      setCentros(c.data || []);
      setPriceLists(pl.data || []);
      // use_pos_price_list_per_site sale del AuthContext
      setUsePosPriceListPerSite(Boolean(organization?.use_pos_price_list_per_site));
    } catch (error) {
      console.error(error);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // --- LOGICA FORMULARIO ---
  const handleOpenModal = (sitio = null) => {
    if (sitio) {
      setEditingId(sitio.id);
      setFormData({
        nombre: sitio.nombre,
        direccion: sitio.direccion || '',
        depositos: sitio.depositos || [],
        cost_center_id: sitio.cost_center_id || '',
        mp_pos_id: sitio.mp_pos_id || '',
        mp_terminal_id: sitio.mp_terminal_id || '',
        price_list_id: sitio.price_list_id || ''
      });
    } else {
      setEditingId(null);
      setFormData({ nombre: '', direccion: '', depositos: [], cost_center_id: '', mp_pos_id: '', mp_terminal_id: '', price_list_id: '' });
    }
    setNewDeposito('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewDeposito('');
  };

  const handleAddDeposito = () => {
    if (!newDeposito.trim()) return;
    setFormData({ ...formData, depositos: [...formData.depositos, newDeposito.trim()] });
    setNewDeposito('');
  };

  const handleRemoveDeposito = (index) => {
    const newDeps = [...formData.depositos];
    newDeps.splice(index, 1);
    setFormData({ ...formData, depositos: newDeps });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.nombre) return toast.error('El nombre es obligatorio');

    try {
      const payload = {
        organization_id: profile.organization_id,
        nombre: formData.nombre,
        direccion: formData.direccion,
        depositos: formData.depositos,
        cost_center_id: formData.cost_center_id || null,
        mp_pos_id: formData.mp_pos_id || null,
        mp_terminal_id: formData.mp_terminal_id || null,
        // ✅ Lista de precios del POS para este sitio (depende del toggle de empresa)
        price_list_id: formData.price_list_id || null
      };

      if (editingId) {
        const { error } = await supabase.from('sitios').update(payload).eq('id', editingId);
        if (error) throw error;
        toast.success('Sitio actualizado');
      } else {
        const { error } = await supabase.from('sitios').insert([payload]);
        if (error) throw error;
        toast.success('Sitio creado');
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleToggleActive = async (sitio) => {
    try {
      const { error } = await supabase.from('sitios').update({ is_active: !sitio.is_active }).eq('id', sitio.id);
      if (error) throw error;
      toast.success(sitio.is_active ? 'Sitio archivado' : 'Sitio reactivado');
      fetchData();
    } catch (err) {
      toast.error('Error al cambiar estado');
    }
  };

  // --- FILTROS Y PROCESAMIENTO ---
  const sitiosFiltrados = useMemo(() => {
    return sitios.filter(s => {
      // 1. View Mode (Active/Archived)
      if (viewMode === 'activos' && !s.is_active) return false;
      if (viewMode === 'archivados' && s.is_active) return false;

      // 2. Busqueda Textual
      const matchText = (s.nombre || '').toLowerCase().includes(busqueda.toLowerCase()) || 
                        (s.direccion || '').toLowerCase().includes(busqueda.toLowerCase());
      
      // 3. Filtro Centro Costo
      const matchCentro = filtroCentro === 'todos' || s.cost_center_id === filtroCentro;

      // 4. Filtro Depositos Logic
      let matchDep = true;
      if (filtroDeposito === 'con_depositos') matchDep = (s.depositos && s.depositos.length > 0);
      if (filtroDeposito === 'sin_depositos') matchDep = (!s.depositos || s.depositos.length === 0);

      return matchText && matchCentro && matchDep;
    });
  }, [sitios, viewMode, busqueda, filtroCentro, filtroDeposito]);

  // KPI CALCS
  const kpis = useMemo(() => {
    const total = sitiosFiltrados.length;
    const totalDepositos = sitiosFiltrados.reduce((acc, s) => acc + (s.depositos?.length || 0), 0);
    const conCentro = sitiosFiltrados.filter(s => s.cost_center_id).length;
    return { total, totalDepositos, conCentro };
  }, [sitiosFiltrados]);

  useEffect(() => {
    if (!showModal || typeof document === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowModal(false);
        setNewDeposito('');
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [showModal]);

  const modalMarkup = showModal && typeof document !== 'undefined'
    ? createPortal(
        <div
          className={`fixed inset-x-0 bottom-0 ${topbarOffsetClass} z-[120] overflow-y-auto overscroll-contain bg-[#0B2A36]/60 px-3 pb-3 pt-3 backdrop-blur-sm animate-in fade-in duration-200 sm:px-4 sm:pb-4 sm:pt-4`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <div className="flex min-h-full w-full items-start justify-center sm:items-center">
            <div
              className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-[1.75rem] border border-white/20 bg-white shadow-2xl animate-in zoom-in-95 duration-200 sm:rounded-3xl"
              onMouseDown={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={editingId ? 'Editar sitio' : 'Nuevo sitio'}
            >
              <div className="shrink-0 border-b border-slate-100 bg-slate-50/95 p-4 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B2A36] text-[#3FF2AA] shadow-sm">
                      <Building2 size={20} />
                    </div>
                    <div className="min-w-0">
                      <h2 className="truncate text-base font-black uppercase tracking-tight text-slate-800 sm:text-lg">
                        {editingId ? 'Editar Sitio' : 'Nuevo Sitio'}
                      </h2>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Configuración de nodo</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="shrink-0 rounded-full border border-slate-200 bg-white p-2 text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-600"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
                <div className="custom-scrollbar flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
                  <div className="space-y-1">
                    <label className="ml-1 text-[9px] font-black uppercase tracking-widest text-slate-400">Nombre Identificador</label>
                    <input
                      autoFocus
                      type="text"
                      placeholder="EJ: SUCURSAL CENTRO, PLANTA NORTE..."
                      className="w-full rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs font-bold uppercase text-slate-700 shadow-inner outline-none transition-all focus:border-[#51A7FD]"
                      value={formData.nombre}
                      onChange={e => setFormData({...formData, nombre: e.target.value})}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="ml-1 text-[9px] font-black uppercase tracking-widest text-slate-400">Dirección Física</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                      <input
                        type="text"
                        placeholder="CALLE, NÚMERO, LOCALIDAD..."
                        className="w-full rounded-xl border border-slate-100 bg-slate-50 py-3 pl-10 pr-4 text-xs font-bold uppercase text-slate-700 shadow-inner outline-none transition-all focus:border-[#51A7FD]"
                        value={formData.direccion}
                        onChange={e => setFormData({...formData, direccion: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="ml-1 text-[9px] font-black uppercase tracking-widest text-slate-400">Centro de Costo (Contable)</label>
                    <div className="relative">
                      <Target className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                      <select
                        className="w-full cursor-pointer appearance-none rounded-xl border border-slate-100 bg-slate-50 py-3 pl-10 pr-4 text-xs font-bold uppercase text-slate-700 shadow-inner outline-none transition-all focus:border-[#51A7FD]"
                        value={formData.cost_center_id}
                        onChange={e => setFormData({...formData, cost_center_id: e.target.value})}
                      >
                        <option value="">Sin Asignar</option>
                        {centros.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="ml-1 flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-[#51A7FD]">
                      ID Caja Mercado Pago <span className="rounded-sm bg-[#51A7FD] px-1.5 py-0.5 text-[7px] text-white">QR IN-STORE</span>
                    </label>
                    <input
                      type="text"
                      placeholder="EJ: SUC001POS001..."
                      className="w-full rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-700 shadow-inner outline-none transition-all placeholder:font-normal focus:border-[#51A7FD]"
                      value={formData.mp_pos_id}
                      onChange={e => setFormData({...formData, mp_pos_id: e.target.value})}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="ml-1 flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-[#51A7FD]">
                      Terminal Point <span className="rounded-sm bg-[#51A7FD] px-1.5 py-0.5 text-[7px] text-white">POSNET</span>
                    </label>

                    <div className="flex gap-2">
                      <select
                        className="w-full cursor-pointer appearance-none rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-700 shadow-inner outline-none transition-all focus:border-[#51A7FD]"
                        value={formData.mp_terminal_id}
                        onChange={e => setFormData({ ...formData, mp_terminal_id: e.target.value })}
                      >
                        <option value="">Sin terminal asignada</option>
                        {/* La terminal ya guardada puede no estar en la lista todavía
                            (si aún no se consultó la cuenta): la mostramos igual para
                            no perderla al guardar. */}
                        {formData.mp_terminal_id
                          && !terminales.some(t => t.id === formData.mp_terminal_id) && (
                          <option value={formData.mp_terminal_id}>{formData.mp_terminal_id}</option>
                        )}
                        {terminales.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.id}{t.operating_mode ? ` — ${t.operating_mode}` : ''}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={cargarTerminales}
                        disabled={cargandoTerminales}
                        title="Buscar las terminales de la cuenta de Mercado Pago"
                        className="shrink-0 rounded-xl border border-slate-100 bg-slate-50 px-3 py-3 text-slate-500 shadow-inner transition-all hover:border-[#51A7FD] hover:text-[#51A7FD] disabled:opacity-40"
                      >
                        <RefreshCcw size={14} className={cargandoTerminales ? 'animate-spin' : ''} />
                      </button>
                    </div>

                    {terminalSeleccionada && terminalSeleccionada.operating_mode !== 'PDV' && (
                      <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="text-[9px] font-black uppercase tracking-wide text-amber-700">
                          La terminal está en modo {terminalSeleccionada.operating_mode || 'desconocido'}:
                          ignora los cobros que le mande el POS.
                        </p>
                        <button
                          type="button"
                          onClick={() => ponerEnModoPdv(terminalSeleccionada.id)}
                          disabled={cargandoTerminales}
                          className="mt-2 rounded-lg bg-amber-500 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-white transition-all hover:bg-amber-600 disabled:opacity-40"
                        >
                          Poner en modo PDV
                        </button>
                      </div>
                    )}

                    {terminalesConsultadas && !terminales.length && (
                      <p className="ml-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
                        No hay terminales en la cuenta. Revisá el Access Token en Configuración → Empresa.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label
                      className={`ml-1 flex items-center gap-2 text-[9px] font-black uppercase tracking-widest ${
                        usePosPriceListPerSite ? 'text-emerald-700' : 'text-slate-400'
                      }`}
                    >
                      Lista de Precios del Punto de Venta
                      <span
                        className={`rounded-sm px-1.5 py-0.5 text-[7px] ${
                          usePosPriceListPerSite
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-300 text-white'
                        }`}
                      >
                        POS
                      </span>
                    </label>
                    <select
                      className="w-full cursor-pointer appearance-none rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-700 shadow-inner outline-none transition-all focus:border-emerald-500"
                      value={formData.price_list_id}
                      onChange={e => setFormData({ ...formData, price_list_id: e.target.value })}
                    >
                      <option value="">- Sin lista (usar precios base) -</option>
                      {priceLists.map(pl => (
                        <option key={pl.id} value={pl.id}>{pl.name}</option>
                      ))}
                    </select>
                    {usePosPriceListPerSite ? (
                      <p className="mt-1 text-[8px] font-bold uppercase tracking-widest leading-relaxed text-emerald-600">
                        Activo: el POS de este sitio usará esta lista para calcular precios y filtrar productos visibles.
                      </p>
                    ) : (
                      <p className="mt-1 text-[8px] font-bold normal-case uppercase tracking-widest leading-relaxed text-slate-400">
                        Atención: esta asignación se guarda pero <span className="font-black">aún no se aplica</span>. Activá la regla "Lista de precios por sitio (POS)" en Mi Empresa {'->'} Reglas operativas.
                      </p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <label className="mb-3 flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                      <Package size={14} /> Configuración de Depósitos
                    </label>

                    <div className="mb-3 flex flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        placeholder="NOMBRE DEL DEPÓSITO..."
                        className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD]"
                        value={newDeposito}
                        onChange={e => setNewDeposito(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddDeposito())}
                      />
                      <button
                        type="button"
                        onClick={handleAddDeposito}
                        className="flex w-full shrink-0 items-center justify-center rounded-lg bg-[#0B2A36] px-3 py-2 text-white transition-all hover:bg-slate-800 sm:w-auto"
                      >
                        <Plus size={16} />
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {formData.depositos.map((dep, index) => (
                        <div key={index} className="group flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
                          <span className="text-[9px] font-black uppercase text-slate-600">{dep}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveDeposito(index)}
                            className="ml-1 text-slate-300 transition-colors hover:text-rose-500"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      {formData.depositos.length === 0 && <p className="text-[9px] italic text-slate-400">No hay depósitos definidos.</p>}
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-3">
                    <Info size={16} className="mt-0.5 shrink-0 text-[#51A7FD]" />
                    <p className="text-[8px] font-bold uppercase italic leading-relaxed text-blue-600">
                      Si asigna un Centro de Costo, el Arqueo de Caja y la Facturación Electrónica imputarán los movimientos a esa unidad de negocio automáticamente.
                    </p>
                  </div>
                </div>

                <div className="shrink-0 border-t border-slate-100 bg-white/95 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:p-6 sm:pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#3FF2AA] py-4 text-[10px] font-black uppercase tracking-[0.2em] text-[#0B2A36] shadow-lg shadow-emerald-500/20 transition-all hover:scale-[1.01] active:scale-95"
                  >
                    <Save size={16} /> Confirmar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;


  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER ESTILO PRODUCTOS */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Store size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Gestión de Sitios</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Sucursales, plantas y depósitos logísticos.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          {/* Toggle View Mode */}
          <div className="flex bg-white rounded-2xl border border-slate-200 p-1 shadow-sm">
             <button 
               onClick={() => setViewMode('activos')}
               className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${viewMode === 'activos' ? 'bg-[#0B2A36] text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
             >
                <CheckCircle2 size={14} /> Activos
             </button>
             <button 
               onClick={() => setViewMode('archivados')}
               className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${viewMode === 'archivados' ? 'bg-slate-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
             >
                <Archive size={14} /> Archivados
             </button>
          </div>

          <button 
            onClick={() => handleOpenModal()} 
            className="px-5 py-2.5 rounded-2xl bg-[#3ff2aa] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 border border-[#3ff2aa]"
          >
            <Plus size={16} strokeWidth={3} /> Nuevo Sitio
          </button>
        </div>
      </div>

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Sucursales</p>
            <Store size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Puntos de venta/acopio</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Red Logística</p>
            <Package size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.totalDepositos}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Depósitos internos</p>
        </div>

        <div className="imp-kpi">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cobertura Fiscal</p>
                <Target size={16} className="text-emerald-500" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
                {Math.round((kpis.conCentro / (kpis.total || 1)) * 100)}%
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Asignados a C. Costo</p>
        </div>
      </div>

      {/* BARRA DE FILTROS Y HERRAMIENTAS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4">
         <div className="flex flex-col md:flex-row items-center gap-3">
             
             {/* Búsqueda */}
             <div className="relative flex-1 w-full">
                 <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                    <Search size={10} /> Buscar
                 </label>
                 <div className="relative">
                    <input 
                        type="text"
                        placeholder="NOMBRE O DIRECCIÓN..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase text-slate-700"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                 </div>
             </div>

             {/* Filtro Centro Costo */}
             <div className="w-full md:w-48">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                    <Target size={10} /> Centro de Costo
                </label>
                <select
                  value={filtroCentro}
                  onChange={(e) => setFiltroCentro(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 p-2.5 rounded-2xl text-[10px] font-bold outline-none cursor-pointer uppercase text-slate-700 h-[38px]"
                >
                  <option value="todos">Todos los centros</option>
                  {centros.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
             </div>

             {/* Layout Toggle */}
             <div className="flex items-end h-[58px]">
                <div className="flex bg-slate-50 rounded-2xl p-1 border border-slate-100 h-[38px]">
                    <button 
                        onClick={() => setLayout('lista')}
                        className={`p-2 rounded-xl transition-all ${layout === 'lista' ? 'bg-white text-[#51A7FD] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <List size={16} />
                    </button>
                    <button 
                        onClick={() => setLayout('grilla')}
                        className={`p-2 rounded-xl transition-all ${layout === 'grilla' ? 'bg-white text-[#51A7FD] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <LayoutGrid size={16} />
                    </button>
                </div>
             </div>
         </div>
      </div>

      {/* CONTENIDO PRINCIPAL (LISTA O GRILLA) */}
      <div className="w-full">
        {loading ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-400 gap-2">
                <RefreshCcw className="animate-spin" size={24} />
                <p className="text-[10px] font-black uppercase tracking-widest">Cargando...</p>
            </div>
        ) : sitiosFiltrados.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-300 gap-2 border-2 border-dashed border-slate-100 rounded-3xl">
                <Store size={40} strokeWidth={1} />
                <p className="text-[10px] font-black uppercase tracking-widest">No se encontraron sitios</p>
            </div>
        ) : layout === 'lista' ? (
            // VISTA LISTA (TABLA ESTILO NOVA)
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto imp-table-wrap">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                <th className="px-5 py-3 pl-5">Sitio</th>
                                <th className="px-4 py-3">Ubicación</th>
                                <th className="px-4 py-3">Infraestructura</th>
                                <th className="px-4 py-3">Imputación</th>
                                <th className="px-4 py-3 text-right pr-5">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {sitiosFiltrados.map(s => (
                                <tr key={s.id} className="hover:bg-blue-50/20 transition-all group">
                                    <td className="px-5 py-3 pl-5">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center border shadow-sm ${s.is_active ? 'bg-white border-slate-100 text-slate-500' : 'bg-slate-50 border-slate-100 text-slate-300'}`}>
                                                <Store size={14} />
                                            </div>
                                            <div>
                                                <p className={`text-[11px] font-black uppercase tracking-tight ${s.is_active ? 'text-slate-700 group-hover:text-[#51A7FD]' : 'text-slate-400 line-through'}`}>
                                                    {s.nombre}
                                                </p>
                                                {!s.is_active && <span className="text-[7px] bg-slate-100 text-slate-400 px-1 rounded border border-slate-200 uppercase font-bold">Archivado</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                                            <MapPin size={12} className="text-slate-300" />
                                            <span className="uppercase truncate max-w-[200px]">{s.direccion || 'Sin dirección'}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {s.depositos?.length > 0 ? (
                                            <div className="flex items-center gap-1">
                                                <span className="bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-lg text-[9px] font-black uppercase">
                                                    {s.depositos.length} Depósitos
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-[9px] font-bold text-slate-300 uppercase italic">Sin depósitos</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                            <Target size={12} className={s.centros_costo ? "text-emerald-500" : "text-slate-200"} />
                                            <span className="text-[9px] font-bold text-slate-600 uppercase">
                                                {s.centros_costo?.nombre || '—'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 pr-5 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => handleOpenModal(s)}
                                                className="p-1.5 text-slate-400 hover:text-[#51A7FD] hover:bg-blue-50 rounded-lg transition-all"
                                                title="Editar"
                                            >
                                                <Edit3 size={14} />
                                            </button>
                                            <button 
                                                onClick={() => handleToggleActive(s)}
                                                className={`p-1.5 rounded-lg transition-all ${s.is_active ? 'text-slate-400 hover:text-rose-500 hover:bg-rose-50' : 'text-emerald-500 hover:bg-emerald-50'}`}
                                                title={s.is_active ? "Archivar" : "Reactivar"}
                                            >
                                                {s.is_active ? <Trash2 size={14} /> : <RefreshCcw size={14} />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        ) : (
            // VISTA GRILLA
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {sitiosFiltrados.map(s => (
                    <div key={s.id} className={`bg-white rounded-2xl border p-5 transition-all hover:shadow-lg group relative ${s.is_active ? 'border-slate-200' : 'border-slate-100 opacity-70 bg-slate-50'}`}>
                        
                        <div className="flex justify-between items-start mb-4">
                            <div className={`p-3 rounded-2xl ${s.is_active ? 'bg-[#0B2A36] text-[#3FF2AA]' : 'bg-slate-200 text-slate-400'}`}>
                                <Store size={20} />
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => handleOpenModal(s)} className="p-2 bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] transition-all shadow-sm">
                                    <Edit3 size={14} />
                                </button>
                                <button onClick={() => handleToggleActive(s)} className="p-2 bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 rounded-xl text-slate-400 hover:text-rose-500 transition-all shadow-sm">
                                    {s.is_active ? <Trash2 size={14} /> : <RefreshCcw size={14} />}
                                </button>
                            </div>
                        </div>

                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-1">{s.nombre}</h3>
                        
                        <div className="space-y-2 mt-3">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase">
                                <MapPin size={12} className="text-slate-300" />
                                <span className="truncate">{s.direccion || 'Sin dirección'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase">
                                <Target size={12} className={s.centros_costo ? "text-emerald-500" : "text-slate-300"} />
                                <span className="truncate">{s.centros_costo?.nombre || 'Sin centro asignado'}</span>
                            </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-slate-100">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Depósitos ({s.depositos?.length || 0})</p>
                            <div className="flex flex-wrap gap-1">
                                {s.depositos?.slice(0, 3).map((d, i) => (
                                    <span key={i} className="px-2 py-1 bg-slate-50 border border-slate-100 rounded-md text-[8px] font-bold text-slate-600 uppercase">
                                        {d}
                                    </span>
                                ))}
                                {(s.depositos?.length || 0) > 3 && (
                                    <span className="px-2 py-1 bg-slate-50 border border-slate-100 rounded-md text-[8px] font-bold text-slate-400">
                                        +{s.depositos.length - 3}
                                    </span>
                                )}
                                {(!s.depositos || s.depositos.length === 0) && <span className="text-[9px] text-slate-300 italic">Ninguno</span>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>

      {/* MODAL FORMULARIO */}

      {modalMarkup}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
};

export default Sitios;
